import { Component } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { compose, withProps } from 'recompose';
import { AssessmentRS, ChartFragment } from 'models/api-response';
import { ELSModalServiceType } from 'models/els';
import { AssignmentType, PhaseProgress, UserRole } from 'models/enum';
import { CaseStudyPhase, CaseStudySequence, NewAssignment, SimChartAssignment, SimulationEhrPhase, SimulationEhrSequence } from 'models/ui';
import { NAV_ID, RouteParams, RoutePath } from 'constants/app.constant';
import { apiHelper, appHelper, assignmentHelper } from 'helpers';
import { caseStudyService, chartService, simulationService } from 'services';
import { appSelectors } from 'redux/ducks/app';
import { studentSelectors } from 'redux/ducks/student';
import AssignmentCommonInfoView, { AssignmentCommonInfoViewProps } from './AssignmentCommonInfoView';
import { ConfirmationModal } from '..';
import { ELSWithModalService } from '../els';
import { instructorSelectors } from 'redux/ducks/instructor';


export enum Tabs {
  INSTRUCTIONS = 0,
  CASE_STUDY_OVERVIEW = 1
}

export interface PhaseInfo {
  title: string;
  instruction?: string;
}

export interface AssignmentCommonInfoProps {
  assessmentId: number;
  sequenceChunk: CaseStudySequence | SimulationEhrSequence;
  modalService: ELSModalServiceType;
  history: History;
  assessment: AssessmentRS;
  setShowQuiz: () => void;
  fullAssignment: SimChartAssignment;
  userRole: string;
  newAssignment?: NewAssignment;
}

export interface NavigateProps {
  current: number;
  total: number;
  disableNext?: boolean;
  onNext: () => void;
  onPrev: () => void;
}

interface AssignmentCommonInfoState {
  activeTab: number;
  current: number;
  selectedPhase: CaseStudyPhase | SimulationEhrPhase;
  isFinalPhase: boolean;
  isMarkPhaseDisabled: boolean;
}

class AssignmentCommonInfo extends Component<AssignmentCommonInfoProps & RouteComponentProps, AssignmentCommonInfoState> {
  static displayName = 'AssignmentCommonInfo';

  constructor(props) {
    super(props);

    const { sequenceChunk } = props;
    let index = sequenceChunk.startedPhaseList.findIndex((item) => item.id === sequenceChunk.phaseId);
    index = index > 0 ? index : 0;
    this.state = {
      activeTab: Tabs.INSTRUCTIONS,
      current: index,
      selectedPhase: sequenceChunk.startedPhaseList[index],
      isFinalPhase: sequenceChunk?.displayOrder === sequenceChunk?.totalPhases,
      isMarkPhaseDisabled: false
    };
  }

  // Added helper to centralize assignment type resolution
  getAssignmentType = (): AssignmentType | undefined => {
    const { fullAssignment, newAssignment } = this.props;
    const type = fullAssignment?.assignmentType ?? newAssignment?.assignmentType;
    if (!type) return undefined;
    return type as AssignmentType;
  };

  handleTabGroupClick = (tabIndex: number) => this.setState({ activeTab: tabIndex });

  handleMarkPhaseDoneClick = () => {
    return appHelper.useLoader(
      chartService.loadFragments({ chartId: this.props.assessment.simChartId }).then(({ data }) => {
        const studentInputData: ChartFragment[] = data?.filter((item) => item.navElementId !== NAV_ID.TIME);
        if (studentInputData.length) {
          this.savePhase();
        } else {
          this.openWarningModal();
        }
      }),
      { errorMessage: 'can not load fragment' }
    );
  };

  openWarningModal = () => {
    const assignmentType = this.getAssignmentType();
    const msg =
      assignmentType === AssignmentType.SIMULATION
        ? 'Please confirm that all student actions listed in the Phase Instructions have been completed. Once you proceed, this phase will no longer be accessible.'
        : 'Before proceeding to the quiz associated with this phase of the case study, please ensure that you have completed all student actions noted in the Phase Instructions.';
    this.props.modalService.openModal({
      modalId: 'warningModal',
      color: 'primary',
      content: (
        <ConfirmationModal
          showIcon
          header="Please confirm."
          message={msg}
          onOkClick={() => {
            this.props.modalService.closeModal('warningModal');
            this.savePhase();
          }}
          onCancelClick={() => this.props.modalService.closeModal('warningModal')}
        />
      )
    });
  };

  handleNextPhase = () => {
    this.setState((prevState) => {
      const { sequenceChunk } = this.props;
      const { current } = prevState;
      const selectedPhase = sequenceChunk.startedPhaseList[current + 1];
      return { current: current + 1, selectedPhase };
    });
  };

  handlePrevPhase = () => {
    this.setState((prevState) => {
      const { sequenceChunk } = this.props;
      const { current } = prevState;
      const selectedPhase = sequenceChunk.startedPhaseList[current - 1];
      return { current: current - 1, selectedPhase };
    });
  };

  savePhase() {
    const assignmentType = this.getAssignmentType();
    if (assignmentType === AssignmentType.SIMULATION) {
      simulationService
        .completePhase(this.props.assessmentId)
        .then(({ data }) => {
          if (!this.state.isFinalPhase) {
            const simulationEhrHome = RoutePath.student.simulationEhrLanding.replace(RouteParams.ASSESSMENT_ID, String(this.props.assessmentId));
            this.props.history.push(simulationEhrHome);
          } else {
            if (this.props.userRole === UserRole.STUDENT) {
              const assignmentSubmission = RoutePath.student.assignmentSubmission.replace(RouteParams.ASSESSMENT_ID, String(this.props.assessmentId));
              this.props.history.push(assignmentSubmission);
            }
            this.setState({isMarkPhaseDisabled: true});
          }
        })
        .catch((err) => apiHelper.showApiError(`can not mark phase done ${err}`));
    } else {
      caseStudyService
        .completePhase(this.props.assessmentId)
        .then(({ data }) => {
          if (data.status === PhaseProgress.IN_PROGRESS) {
            this.props.setShowQuiz();
          }
          if (data.status === PhaseProgress.COMPLETED) {
            const caseStudyHome = RoutePath.student.caseStudyLanding.replace(RouteParams.ASSESSMENT_ID, String(this.props.assessmentId));
            this.props.history.push(caseStudyHome);
          }
        })
        .catch((err) => apiHelper.showApiError(`can not mark phase done ${err}`));
    }
  }

  render() {
    const { sequenceChunk, assessment } = this.props;
    const { activeTab, selectedPhase, current, isMarkPhaseDisabled } = this.state;
    const assignmentType = this.getAssignmentType();
    let overview = '';
    if (assignmentType === AssignmentType.SIMULATION && 'simulationEhrOverview' in sequenceChunk) {
      overview = sequenceChunk.simulationEhrOverview?.overview || '';
    } else if ('caseStudyOverview' in sequenceChunk) {
      overview = sequenceChunk.caseStudyOverview?.overview || '';
    }
    const markPhaseDoneIsDisabled = (sequenceChunk.status !== PhaseProgress.IN_PROGRESS || isMarkPhaseDisabled);
    const navigateProps = {
      current: current + 1,
      total: sequenceChunk.totalPhases,
      disableNext: current + 1 === sequenceChunk.startedPhaseList.length,
      onNext: this.handleNextPhase,
      onPrev: this.handlePrevPhase
    };
    const markPhaseDoneText = assignmentHelper.isAssessmentSubmitted(assessment.status) ? 'All Phases Marked Done' : `Mark ${sequenceChunk.content.title} as Done`;

    assignmentHelper.resetEmptyEhrDemoStorage();

    const viewProps: AssignmentCommonInfoViewProps = {
      activeTab,
      overview,
      markPhaseDoneText,
      instruction: selectedPhase?.instruction,
      navigateProps,
      onTabGroupClick: this.handleTabGroupClick,
      onMarkPhaseDoneClick: this.handleMarkPhaseDoneClick,
      markPhaseDoneIsDisabled,
      assignmentType
    };

    return <AssignmentCommonInfoView {...viewProps} />;
  }
}

const mapStateToProps = (state) => ({
  assessment: appSelectors.getAssessment(state),
  fullAssignment: studentSelectors.getFullAssignment(state),
  userRole: appSelectors.getUserRole(state),
  newAssignment: instructorSelectors.getNewAssignment(state)
});

export { AssignmentCommonInfo as BaseAssignmentCommonInfo };
export default compose(
  withProps(() => ({
    history: useHistory()
  })),
  ELSWithModalService,
  connect(mapStateToProps)
)(AssignmentCommonInfo);